import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';
import { ArrowRight } from 'lucide-react';

const processImages = [
  "https://www.cobras.be/sites/default/files/styles/card_medium/public/uploads/images/cobras_fabriek_08.jpg",
  "https://www.cobras.be/sites/default/files/styles/card_medium/public/uploads/images/cobras_website_18_vierkant_0.jpg",
  "https://www.cobras.be/sites/default/files/styles/card_medium/public/uploads/images/cobras_fabriek_12.jpg",
];
const processLinks = ['/ambitiedocument', '/doordacht-design', '/uitvoering'];

const ProcessCard = ({ step, image, link, index, readMore }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setIsVisible(true); }, { threshold: 0.2 });
    if (cardRef.current) obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={cardRef} className="group cursor-pointer" onClick={() => navigate(link)} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(60px)', transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * 150}ms` }}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm mb-5">
        <img src={image} alt={step.title} className="w-full h-full object-cover transition-transform duration-700" style={{ transform: isHovered ? 'scale(1.08)' : 'scale(1)' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-500" style={{ opacity: isHovered ? 1 : 0.3 }} />
      </div>
      <h3 className="text-xl md:text-2xl font-bold text-white mb-3">{step.title}</h3>
      <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-4">{step.desc}</p>
      <span className="inline-flex items-center gap-2 text-sm font-bold tracking-wider uppercase" style={{ color: '#A855F7' }}>
        {readMore} <ArrowRight size={16} className="transition-transform duration-300" style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }} />
      </span>
    </div>
  );
};

const ProcessSection = () => {
  const { lang } = useLanguage();
  const { t, get } = useTranslation(lang);
  const [headingVisible, setHeadingVisible] = useState(false);
  const headingRef = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeadingVisible(true); }, { threshold: 0.3 });
    if (headingRef.current) obs.observe(headingRef.current);
    return () => obs.disconnect();
  }, []);

  const steps = t.process.steps[lang] || t.process.steps.nl;

  return (
    <section id="ambitiedocument" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#0a0a0a' }}>
      <div className="absolute inset-0 opacity-20" style={{backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,backgroundSize:'200px'}} />
      <div className="relative z-10 px-8 md:px-16 lg:px-24">
        <div ref={headingRef} className="mb-16 max-w-[900px]" style={{ opacity: headingVisible ? 1 : 0, transform: headingVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white italic leading-tight">{get(t.process.heading)}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {steps.map((step, index) => (
            <ProcessCard key={index} step={step} image={processImages[index]} link={processLinks[index]} index={index} readMore={get(t.process.readMore)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
